import {cyclesColor, cyclesVec3} from "#material/cycles/helpers"
import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {BsdfPrincipledSockets} from "#material/nodes/shader/bsdf-principled/definition"
import {bsdfPrincipledDefinition} from "#material/nodes/shader/bsdf-principled/definition"
import {cyclesDistribution, cyclesSurfaceMethod} from "#material/nodes/shader/bsdf-principled/helpers"
import {asVec3, threeConvert, threeNormalFromVec3, threeRGBColor, threeValue, threeVec3} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {GraphNode} from "@cm/graph-v3"
import {registerMaterialGraphNode} from "#material/register"
import type {Three as THREE} from "#material/three/exports"
import {ThreeTsl as TSL} from "#material/three/exports"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {ThreeReturns} from "#material/sockets/three"
import type {InputMaterialConnection} from "#material/sockets/definition"
import {value} from "#material/nodes/input/value/shorthand"
import {add, div, gt, mul, sqrt, square, sub} from "#material/nodes/converter/scalar-math/shorthands"
import {color} from "#material/nodes/input/color/shorthand"
import {separateRgb} from "#material/nodes/converter/separate-rgb/shorthand"
import type {PrincipledBsdfNode} from "#material/cycles/types"
import {combineRgb} from "#material/nodes/converter/combine-rgb/shorthand"
import {defaults} from "#material/nodes/shader/bsdf-principled/constants"
import {Value} from "#material/nodes/input/value/value"
import {ThreeColormassMaterial} from "#material/three/three-colormass-material"

@registerMaterialGraphNode
export class BsdfPrincipled extends MaterialNode<BsdfPrincipledSockets> {
    public override get definition() {
        return bsdfPrincipledDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<BsdfPrincipledSockets>> => {
        const {logger} = evaluator.context
        const material = new ThreeColormassMaterial()

        const {anisotropic, anisotropicRotation, tangent, alpha, subsurface, subsurfaceRadius, subsurfaceIor, subsurfaceAnisotropy, ...relevantInputs} =
            this.parameters.inputs

        const {
            baseColor,
            metallic,
            roughness,
            specular,
            specularTint,
            ior,
            sheen,
            sheenTint,
            clearcoat,
            clearcoatRoughness,
            clearcoatNormal,
            normal,
            emission,
            emissionStrength,
            transmission,
        } = await evaluator.getAll(relevantInputs)

        material.colorNode = baseColor?.toColor() ?? threeConvert(this.parameters.settings.baseColor, threeRGBColor) ?? threeRGBColor(defaults.baseColor)
        material.roughnessNode = roughness?.toFloat() ?? threeConvert(this.parameters.settings.roughness, threeValue) ?? material.roughnessNode
        material.metalnessNode = metallic?.toFloat() ?? threeConvert(this.parameters.settings.metallic, threeValue) ?? material.metalnessNode

        const specularValue = specular?.toFloat() ?? threeConvert(this.parameters.settings.specular, threeValue) ?? threeValue(defaults.specular)
        material.iorNode = TSL.sub(TSL.div(2.0, TSL.sub(1.0, TSL.sqrt(TSL.mul(specularValue, 0.08)))), 1.0)

        const specularTintValue =
            specularTint?.toFloat() ?? threeConvert(this.parameters.settings.specularTint ?? defaults.specularTint, threeValue, (val) => val > 0)
        if (specularTintValue && material.colorNode)
            material.specularColorNode = TSL.mix(
                threeRGBColor({
                    r: 1,
                    g: 1,
                    b: 1,
                }),
                material.colorNode.rgb,
                specularTintValue,
            )

        const sheenValue = sheen?.toFloat() ?? threeConvert(this.parameters.settings.sheen, threeValue, (val) => val > 0) ?? material.sheenNode
        if (sheenValue) {
            const sheenTintValue = sheenTint?.toFloat() ?? threeConvert(this.parameters.settings.sheenTint, threeValue)
            if (sheenTintValue && material.colorNode) material.sheenNode = TSL.mul(material.colorNode.rgb, sheenTintValue)
            else material.sheenNode = sheenValue
        }

        material.clearcoatNode =
            clearcoat?.toFloat() ?? threeConvert(this.parameters.settings.clearcoat, threeValue, (val) => val > 0) ?? material.clearcoatNode
        if (material.clearcoatNode) {
            material.clearcoatRoughnessNode =
                clearcoatRoughness?.toFloat() ?? threeConvert(this.parameters.settings.clearcoatRoughness, threeValue) ?? material.clearcoatRoughnessNode
            material.clearcoatNormalNode =
                clearcoatNormal?.toVec3() ??
                threeConvert(this.parameters.settings.clearcoatNormal, threeVec3, (val) => val.x !== 0 || val.y !== 0 || val.z !== 0) ??
                material.clearcoatNormalNode
        }

        material.normalNode =
            normal?.toVec3() ?? (this.parameters.settings.normal ? threeNormalFromVec3(this.parameters.settings.normal) : undefined) ?? material.normalNode //TODO: apply displacement even if original material does not have a normal map

        const emissionValue =
            emission?.toColor() ??
            threeConvert(this.parameters.settings.emission, threeRGBColor, (val) => val.r > 0 || val.g > 0 || val.b > 0) ??
            material.emissiveNode
        if (emissionValue) {
            const emissionStrengthValue = emissionStrength?.toFloat() ?? threeConvert(this.parameters.settings.emissionStrength, threeValue)
            if (emissionStrengthValue) material.emissiveNode = TSL.mul(asVec3(emissionValue), emissionStrengthValue)
            else material.emissiveNode = emissionValue
        }

        const evaluateAlpha = async (alpha: InputMaterialConnection<"float"> | undefined): Promise<THREE.Node<"float"> | undefined> => {
            if (alpha instanceof GraphNode) {
                const {key, value} = alpha.parameters
                if (value instanceof Value && key === "value") {
                    const alphaValue = (value as Value).parameters.settings.value ?? 0
                    if (alphaValue < 0.95) return evaluator.get(alpha)
                    else return undefined
                }
            }

            return evaluator.get(alpha)
        }

        const alphaValue =
            (await evaluateAlpha(this.parameters.inputs.alpha))?.toFloat() ??
            threeConvert(this.parameters.settings.alpha, threeValue, (val) => val < 0.95) ??
            material.opacityNode
        const transmissionValue =
            transmission?.toFloat() ?? threeConvert(this.parameters.settings.transmission, threeValue, (val) => val > 0.05) ?? material.transmissionNode

        if (alphaValue) {
            if (transmissionValue) logger.warn("Material uses both alpha and transmission! Preferring alpha.")
            material.transparent = true
            material.opacityNode = alphaValue
        } else if (transmissionValue) {
            material.transparent = true
            material.transmissionNode = transmissionValue
            material.transmission = 0.01
        }

        return {bsdf: material}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<BsdfPrincipledSockets>> => {
        const two = value(2.0)
        const one = value(1.0)

        // specular ior level adjustment
        // see diff for src\kernel\svm\closure.h between our Cycles 3.6 and 4.2 forks
        const ior = this.parameters.inputs.ior ?? value(this.parameters.settings.ior ?? defaults.ior)

        const specular = this.parameters.inputs.specular ?? value(this.parameters.settings.specular ?? defaults.specular)

        const fresnelF0 = (ior: InputMaterialConnection<"float">) => {
            const one = value(1.0)
            return square(div(sub(ior, one), add(ior, one)))
        }

        const targetIor = sub(div(two, sub(one, sqrt(mul(specular, value(0.08))))), one)
        const targetF0 = fresnelF0(targetIor)

        const F0 = fresnelF0(ior)
        const specularIorLevel = div(targetF0, mul(two, F0))

        // specular tint adjustment
        // see diff for src\kernel\svm\closure.h between our Cycles 3.6 and 4.2 forks
        // specular tint was also changed from float (with default 0.0) to color (with default white)
        const baseColor = this.parameters.inputs.baseColor ?? color(this.parameters.settings.baseColor ?? defaults.baseColor)

        const specularTint = this.parameters.inputs.specularTint ?? value(this.parameters.settings.specularTint ?? defaults.specularTint)

        const {r: baseColorR, g: baseColorG, b: baseColorB} = separateRgb(baseColor)

        const luminance = add(add(mul(value(0.3), baseColorR), mul(value(0.6), baseColorG)), mul(value(0.1), baseColorB))
        const tintWeight = gt(luminance, value(0.0))

        const oneMinusTintWeight = sub(one, tintWeight)
        const tintR = add(mul(tintWeight, div(baseColorR, luminance)), oneMinusTintWeight)
        const tintG = add(mul(tintWeight, div(baseColorG, luminance)), oneMinusTintWeight)
        const tintB = add(mul(tintWeight, div(baseColorB, luminance)), oneMinusTintWeight)

        const oneMinusSpecularTint = sub(one, specularTint)
        const specularTintNewR = add(oneMinusSpecularTint, mul(tintR, specularTint))
        const specularTintNewG = add(oneMinusSpecularTint, mul(tintG, specularTint))
        const specularTintNewB = add(oneMinusSpecularTint, mul(tintB, specularTint))

        const specularTintNew = combineRgb(specularTintNewR, specularTintNewG, specularTintNewB)

        const emissionColor =
            this.parameters.inputs.emission ?? undefined /*(this.parameters.settings.emission ? color(this.parameters.settings.emission) : undefined)*/ //In the old material converter the emission parameter was somehow removed if it was set

        const bsdf: PrincipledBsdfNode = {
            type: "principled_bsdf",
            inputs: await evaluator.getAll({
                alpha: this.parameters.inputs.alpha,
                anisotropic: this.parameters.inputs.anisotropic,
                anisotropic_rotation: this.parameters.inputs.anisotropicRotation,
                base_color: baseColor,
                coat_ior: undefined,
                coat_roughness: this.parameters.inputs.clearcoatRoughness,
                coat_normal: this.parameters.inputs.clearcoatNormal,
                coat_tint: undefined,
                coat_weight: this.parameters.inputs.clearcoat,
                emission_color: emissionColor,
                emission_strength: this.parameters.inputs.emissionStrength,
                ior: ior,
                metallic: this.parameters.inputs.metallic,
                normal: this.parameters.inputs.normal ?? undefined,
                roughness: this.parameters.inputs.roughness ?? undefined,
                sheen_roughness: undefined,
                sheen_tint: this.parameters.inputs.sheenTint,
                sheen_weight: this.parameters.inputs.sheen,
                specular_ior_level: specularIorLevel,
                specular_tint: specularTintNew,
                subsurface_anisotropy: this.parameters.inputs.subsurfaceAnisotropy,
                subsurface_ior: this.parameters.inputs.subsurfaceIor,
                subsurface_radius: this.parameters.inputs.subsurfaceRadius,
                subsurface_scale: this.parameters.inputs.subsurfaceScale,
                subsurface_weight: this.parameters.inputs.subsurface,
                surface_mix_weight: undefined,
                tangent: this.parameters.inputs.tangent ?? undefined,
                thin_film_ior: undefined,
                thin_film_thickness: undefined,
                transmission_weight: this.parameters.inputs.transmission,
            }),
            parameters: {
                alpha: this.parameters.settings.alpha,
                anisotropic: this.parameters.settings.anisotropic,
                anisotropic_rotation: this.parameters.settings.anisotropicRotation,
                base_color: cyclesColor(this.parameters.settings.baseColor),
                coat_ior: undefined,
                coat_normal: cyclesVec3(this.parameters.settings.clearcoatNormal),
                coat_roughness: this.parameters.settings.clearcoatRoughness,
                coat_tint: undefined,
                coat_weight: this.parameters.settings.clearcoat,
                emission_color: undefined,
                distribution: cyclesDistribution(this.parameters.settings.distribution),
                emission_strength: emissionColor ? 1 : 0,
                ior: this.parameters.settings.ior,
                metallic: this.parameters.settings.metallic,
                normal: cyclesVec3(this.parameters.settings.normal),
                roughness: this.parameters.settings.roughness,
                sheen_roughness: undefined,
                sheen_tint:
                    this.parameters.settings.sheenTint !== undefined
                        ? [this.parameters.settings.sheenTint, this.parameters.settings.sheenTint, this.parameters.settings.sheenTint]
                        : undefined,
                sheen_weight: this.parameters.settings.sheen,
                specular_ior_level: this.parameters.settings.specular,
                specular_tint:
                    this.parameters.settings.specularTint !== undefined
                        ? [this.parameters.settings.specularTint, this.parameters.settings.specularTint, this.parameters.settings.specularTint]
                        : undefined,
                subsurface_anisotropy: this.parameters.settings.subsurfaceAnisotropy,
                subsurface_ior: this.parameters.settings.subsurfaceIor,
                subsurface_method: cyclesSurfaceMethod(this.parameters.settings.subsurfaceMethod),
                subsurface_radius: cyclesVec3(this.parameters.settings.subsurfaceRadius),
                subsurface_scale: this.parameters.settings.subsurfaceScale,
                subsurface_weight: this.parameters.settings.subsurface,
                surface_mix_weight: undefined,
                tangent: cyclesVec3(this.parameters.settings.tangent),
                thin_film_ior: undefined,
                thin_film_thickness: undefined,
                transmission_weight: this.parameters.settings.transmission,
            },
            resources: {},
        }

        return {bsdf: [bsdf, "bsdf"]}
    }
}
